import { useEffect, useState } from "react";

/** One less than Tailwind's `sm`, so this and `sm:` never disagree. */
const NARROW = "(max-width: 639px)";

/**
 * Phone or not, as React state — the JS side of the `sm:` breakpoint, for the
 * cases a class cannot express: a pixel width handed to an inline style, a
 * different element tree, a default that only makes sense on a phone.
 *
 * Subscribed, not read once: the room's views are laid out from these numbers,
 * and a rotation that left them at their old value laid out the wrong screen.
 */
export function useIsNarrow(): boolean {
  return useMedia(NARROW);
}

/** The complement of the `wide:` variant (web-shared/src/styles/theme.css):
 * a phone, upright OR on its side. */
const COMPACT = "(max-width: 639px), (pointer: coarse) and (max-height: 500px)";

/**
 * useIsNarrow for the room, whose classes use `wide:` rather than `sm:`: also
 * true on a phone held sideways, which is wider than `sm` but has a phone's
 * height and a finger, so it gets the phone's layout.
 */
export function useIsCompact(): boolean {
  return useMedia(COMPACT);
}

/**
 * A screen with a phone's height — a phone on its side. Its lanes have to be
 * short: at a portrait phone's lane height, 390px of screen less the room's
 * chrome showed two tracks of a five-track Set.
 */
export function useIsShort(): boolean {
  return useMedia("(max-height: 500px)");
}

/** A desktop-wide window (Tailwind's `lg`): room for an inspector column
 * beside a list, as the sample managers lay it out. */
export function useIsWide(): boolean {
  return useMedia("(min-width: 1024px)");
}

function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}
